<template>
  <div class="enterprise-hero-wrap">
    <div class="enterprise-region-strip">
      <div class="enterprise-shell enterprise-region-strip__inner">
        <div class="enterprise-region-strip__label">
          <img :src="regionShieldIcon" alt="">
          <span>{{ regionBanner }}</span>
        </div>
        <div class="enterprise-region-strip__links">
          <button type="button" @click="scrollToPricing">{{ t('enterprise_home_hero_global_prompt') }}</button>
          <a :href="counterpartSiteUrl" target="_blank" rel="noopener noreferrer">
            <span>{{ t(isVipSite ? 'enterprise_home_visit_ai' : 'enterprise_home_visit_vip') }}</span>
            <img :src="externalLinkIcon" alt="">
          </a>
        </div>
      </div>
    </div>

    <section class="enterprise-hero" aria-labelledby="enterprise-hero-title">
      <div class="enterprise-shell enterprise-hero__inner">
        <div class="enterprise-hero__copy">
          <div class="enterprise-kicker">
            <img :src="enterpriseBuildingIcon" alt="">
            {{ t(isVipSite ? 'enterprise_home_hero_kicker_cn' : 'enterprise_home_hero_kicker_global') }}
          </div>
          <h1 id="enterprise-hero-title">{{ t('enterprise_home_hero_title') }}</h1>
          <p class="enterprise-hero__description">
            {{ t(isVipSite ? 'enterprise_home_hero_description_cn' : 'enterprise_home_hero_description_global') }}
          </p>

          <ul class="enterprise-check-list">
            <li>
              <img :src="benefitCheckIcon" alt="">
              <span>{{ t(isVipSite ? 'enterprise_home_hero_benefit_storage_cn' : 'enterprise_home_hero_benefit_storage_global') }}</span>
            </li>
            <li><img :src="benefitCheckIcon" alt=""><span>{{ t('enterprise_home_hero_benefit_glossary') }}</span></li>
            <li><img :src="benefitCheckIcon" alt=""><span>{{ t('enterprise_home_hero_benefit_usage') }}</span></li>
            <li>
              <img :src="benefitCheckIcon" alt="">
              <span>{{ paymentSupport }}</span>
            </li>
          </ul>

          <div class="enterprise-hero__actions">
            <NuxtLink :to="localePath('/account?menu=home')" class="enterprise-button">{{ t('enterprise_home_action_use') }}</NuxtLink>
            <button type="button" class="enterprise-button enterprise-button--secondary" @click="openSupport">{{ t('enterprise_home_action_contact') }}</button>
          </div>

          <p class="enterprise-hero__trust">
            {{ t(isVipSite ? 'enterprise_home_hero_trust_cn' : 'enterprise_home_hero_trust_global') }}
          </p>
        </div>

        <div class="enterprise-queue-card" :aria-label="t('enterprise_home_queue_aria')">
          <div class="enterprise-queue-card__topbar">
            <span><i class="enterprise-queue-card__window enterprise-queue-card__window--red" /><i class="enterprise-queue-card__window enterprise-queue-card__window--yellow" /><i class="enterprise-queue-card__window enterprise-queue-card__window--green" /></span>
            <strong>{{ t('enterprise_home_queue_title') }}</strong>
            <span class="enterprise-queue-card__langs">
              <b>{{ t('enterprise_home_queue_source_language') }}</b>
              <img :src="queueArrowIcon" alt="">
              <b>{{ t('enterprise_home_queue_target_language') }}</b>
            </span>
          </div>
          <div class="enterprise-queue-card__body">
            <div v-for="file in queueFiles" :key="file.name" class="enterprise-queue-file">
              <span class="enterprise-file-icon" :class="`enterprise-file-icon--${file.type}`">
                <img :src="file.icon" alt="">
              </span>
              <div class="enterprise-queue-file__main">
                <div class="enterprise-queue-file__top">
                  <strong>{{ file.name }}</strong>
                  <span>{{ file.language }}</span>
                </div>
                <div class="enterprise-queue-file__bottom">
                  <div :class="['enterprise-progress', `is-${file.statusType}`]"><span :style="{ width: `${file.progress}%` }" /></div>
                  <span :class="['enterprise-queue-file__status', `is-${file.statusType}`]">{{ file.status }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="enterprise-queue-card__footer">
            <span class="enterprise-queue-card__terminology">
              <img :src="terminologyIcon" alt="">
              <span>{{ t('enterprise_home_queue_glossary') }}</span>
            </span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { useSiteConfig } from '~/config/useSiteConfig'
import benefitCheckIcon from '~/assets/images/enterprise/icons/hero-benefit-check.svg'
import enterpriseBuildingIcon from '~/assets/images/enterprise/icons/hero-enterprise-building.svg'
import externalLinkIcon from '~/assets/images/enterprise/icons/hero-external-link.svg'
import fileDocIcon from '~/assets/images/enterprise/icons/hero-file-doc.svg'
import filePdfIcon from '~/assets/images/enterprise/icons/hero-file-pdf.svg'
import fileXlsIcon from '~/assets/images/enterprise/icons/hero-file-xls.svg'
import queueArrowIcon from '~/assets/images/enterprise/icons/hero-queue-arrow.svg'
import regionShieldIcon from '~/assets/images/enterprise/icons/hero-region-shield.svg'
import terminologyIcon from '~/assets/images/enterprise/icons/hero-terminology.svg'

const localePath = useLocalePath()
const { t } = useI18n()
const { isVipSite, counterpartSiteUrl } = useSiteConfig()
const regionBanner = computed(() => t(isVipSite.value ? 'enterprise_home_region_cn' : 'enterprise_home_region_global'))
const paymentSupport = computed(() => t(isVipSite.value ? 'enterprise_home_payment_cn' : 'enterprise_home_payment_global'))
const queueFiles = computed(() => [
  { name: t('enterprise_home_queue_file_contract'), type: 'pdf', icon: filePdfIcon, language: t('enterprise_home_queue_language_zh_en'), status: t('enterprise_home_queue_status_done'), statusType: 'done', progress: 100 },
  { name: t('enterprise_home_queue_file_product'), type: 'doc', icon: fileDocIcon, language: t('enterprise_home_queue_language_zh_ja'), status: t('enterprise_home_queue_status_working'), statusType: 'working', progress: 62.1 },
  { name: t('enterprise_home_queue_file_finance'), type: 'xls', icon: fileXlsIcon, language: t('enterprise_home_queue_language_zh_en'), status: t('enterprise_home_queue_status_uploading'), statusType: 'uploading', progress: 37.5 },
])

function scrollToPricing() {
  document.getElementById('pro-view')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function openSupport() {
  window.$crisp?.do?.('chat:open')
}
</script>
